import { createContext, useContext, useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useDispatch as useReduxDispatch, useSelector, useStore } from 'react-redux';
import axios from 'axios';

import { NotificationService } from 'ui-components/services/notification-service';
import { GuidService } from 'ui-components/services/guid-service';
import { jobGuidManagerFactory } from '@shipstation/async-client-manager';
import { mapPascalCaseQuoteOptions } from '@shipstation/client-common/pusher/hooks/use-freight-quote-progress';
import { freightListener } from '../../../../services/pusher-service/persistent-listeners';
import { createPusherMessageCache } from '../../../../store/pusher/message-cache';

import { entitiesLoad } from '../../../../store/entities/entities.actions';
import { getFreightGridRequest } from '../../../../store/ltl/grid/ltl.grid.actions';
import Global from '../../../../store/global-selectors';
import { WeightUnit } from '../../../../enums/weight-unit';
import { DimensionUnit } from '../../../../enums/dimension-unit';
import type {
  FreightClass,
  FreightPackagingType,
  LtlAddress,
  LtlHandlingUnit,
  LtlCommodity,
  LtlAdditionalServices,
  LtlBookingDetails,
  LtlQuote,
  LtlBookingConfirmation,
} from '@shipstation/client-common/types/ltl';
import type { WizardOpenConfig } from '../../../../store/ltl/ltl.types';

// ---------------------------------------------------------------------------
// Pure payload mappers (module scope — no component state dependencies)
// ---------------------------------------------------------------------------

const mapRawHandlingUnits = (raw: Record<string, unknown>[]): LtlHandlingUnit[] =>
  (raw ?? []).map((hu, index) => ({
    id: String(index + 1),
    packagingType: (hu.packagingType as FreightPackagingType) ?? 'PLT',
    quantity: (hu.quantity as number) ?? 1,
    length: (hu.length as number) ?? 0,
    width: (hu.width as number) ?? 0,
    height: (hu.height as number) ?? 0,
    isStackable: (hu.isStackable as boolean) ?? false,
    commodities: ((hu.commodities as Record<string, unknown>[]) ?? []).map((c: Record<string, unknown>, cIndex: number) => ({
      id: String(cIndex + 1),
      description: (c.description as string) ?? '',
      freightClass: (c.freightClass as FreightClass) ?? '_50',
      nmfcCode: (c.nmfcCode as string) ?? '',
      quantity: (c.quantity as number) ?? 1,
      weight: (c.weight as number) ?? 0,
      isHazmat: (c.isHazmat as boolean) ?? false,
      hazmatDetails: null,
      insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
    })),
  }));

// Used for REST responses (WebV3Api serializes with camelCase)
const mapRawQuoteOptions = (raw: Record<string, unknown>[]): LtlQuote[] =>
  (raw ?? []).map((q) => ({
    offerId: (q.offerId as string) ?? '',
    productTransactionId: (q.productTransactionId as string) ?? '',
    carrierName: (q.carrierName as string) ?? '',
    carrierCode: (q.carrierCode as string) ?? '',
    serviceName: (q.serviceName as string) ?? '',
    totalCharges: (q.totalCharges as number) ?? 0,
    transitDays: (q.transitDays as number) ?? 0,
    estimatedDeliveryDate: (q.estimatedDeliveryDate as string) ?? '',
    isGuaranteed: (q.isGuaranteed as boolean) ?? false,
    quoteExpirationDate: (q.quoteExpirationDate as string) ?? null,
    serviceType: (q.serviceType as string) ?? null,
    originTerminalCode: (q.originTerminalCode as string) ?? null,
    destinationTerminalCode: (q.destinationTerminalCode as string) ?? null,
    insuredAmount: (q.insuredAmount as number) ?? null,
    insurancePremium: (q.insurancePremium as number) ?? null,
    insuranceCertificateNumber: (q.insuranceCertificateNumber as string) ?? null,
  }));

// ---------------------------------------------------------------------------
// Factory helpers
// ---------------------------------------------------------------------------

export const createEmptyCommodity = (): LtlCommodity => ({
  id: GuidService.newGuid(),
  description: '',
  freightClass: '',
  nmfcCode: '',
  quantity: 1,
  weight: 0,
  isHazmat: false,
  hazmatDetails: null,
  insurance: {
    isInsured: false,
    commodityCategoryCode: '',
    condition: '',
    declaredValue: 0,
  },
});

export const createEmptyHandlingUnit = (): LtlHandlingUnit => ({
  id: GuidService.newGuid(),
  packagingType: 'PLT',
  quantity: 1,
  length: 0,
  width: 0,
  height: 0,
  isStackable: false,
  commodities: [createEmptyCommodity()],
});

// ---------------------------------------------------------------------------
// Wizard step types
// ---------------------------------------------------------------------------

export type WizardStep = 'ship-from' | 'ship-to' | 'handling-units' | 'quotes' | 'additional-details';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

type FreightQuoteState = {
  selectedShipFromId: string;
  originAddress: LtlAddress;
  destinationAddress: LtlAddress;
  shipmentDate: string;
  handlingUnits: LtlHandlingUnit[];
  additionalServices: LtlAdditionalServices;
  bookingDetails: LtlBookingDetails;
  quotes: LtlQuote[];
  selectedQuote: LtlQuote | null;
  bookingConfirmation: LtlBookingConfirmation | null;
  isLoadingQuotes: boolean;
  isQuotesStale: boolean;
  isBookingShipment: boolean;
  isSavingQuote: boolean;
  isRefreshingShipment: boolean;
  hasQuoteError: boolean;
  hasBookingError: boolean;
  quoteErrorMessage: string;
  bookingErrorMessage: string;
  savedQuoteFreightShipmentId: string | null;
  drawerView: 'quote-wizard' | 'booking-wizard' | 'booking-confirmation';
  viewingShipmentId: string | null;
  hasCompletedSignup: boolean;
  // Wizard accordion state
  activeStep: WizardStep;
  completedSteps: Partial<Record<WizardStep, boolean>>;
};

const emptyAddress: LtlAddress = {
  name: '',
  company: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  countryCode: '',
  phone: '',
  email: '',
  locationType: '',
};

const emptyAdditionalServices: LtlAdditionalServices = {
  isLiftgatePickup: false,
  isInsidePickup: false,
  isResidentialPickup: false,
  isLimitedAccessPickup: false,
  isFlatbedPickup: false,
  isDockLevelPickup: false,
  isTopLoadPickup: false,
  isConstructionSitePickup: false,
  isTradeShowPickup: false,
  isNotifyBeforePickup: false,
  isLiftgateDelivery: false,
  isInsideDelivery: false,
  isResidentialDelivery: false,
  isLimitedAccessDelivery: false,
  isFlatbedDelivery: false,
  isDockLevelDelivery: false,
  isConstructionSiteDelivery: false,
  isTradeShowDelivery: false,
  isAppointmentDelivery: false,
  isCallBeforeDelivery: false,
  isSortAndSegregate: false,
  isNotifyBeforeDelivery: false,
  isHazmat: false,
  isTemperatureControl: false,
  isProtectFromFreezing: false,
  isOverlength8to12ft: false,
  isOverlength12to20ft: false,
  isOverlength20ftPlus: false,
  isGuaranteedDelivery: false,
  isBlindShipment: false,
  isMarkAndTag: false,
  isCOD: false,
};

const emptyBookingDetails: LtlBookingDetails = {
  pickupDate: '',
  pickupReadyTime: '',
  pickupCloseTime: '',
  isSelfScheduled: false,
  specialInstructions: '',
  referenceNumber: '',
  pickupInstructions: '',
  deliveryInstructions: '',
};

// Seed data used to hand off in-memory form state from the Freight Rate Browser
// straight into the booking wizard, skipping the quote-wizard config + Pusher round-trip.
export type FreightQuoteSeedData = {
  selectedShipFromId?: string;
  destinationAddress?: LtlAddress;
  handlingUnits?: LtlHandlingUnit[];
  additionalServices?: LtlAdditionalServices;
  quotes?: LtlQuote[];
  selectedQuote?: LtlQuote | null;
};

// Lazy initializer so each mount gets a fresh handling unit with a unique GUID.
// When `seed` is provided, the wizard opens directly on the booking-wizard view
// with the earlier steps pre-completed from the browser's form state + selected quote.
const createInitialState = (seed?: FreightQuoteSeedData | null): FreightQuoteState => {
  const base: FreightQuoteState = {
    selectedShipFromId: '',
    originAddress: emptyAddress,
    destinationAddress: emptyAddress,
    shipmentDate: '',
    handlingUnits: [createEmptyHandlingUnit()],
    additionalServices: emptyAdditionalServices,
    bookingDetails: emptyBookingDetails,
    quotes: [],
    selectedQuote: null,
    bookingConfirmation: null,
    isLoadingQuotes: false,
    isQuotesStale: false,
    isBookingShipment: false,
    isSavingQuote: false,
    isRefreshingShipment: false,
    hasQuoteError: false,
    hasBookingError: false,
    quoteErrorMessage: '',
    bookingErrorMessage: '',
    savedQuoteFreightShipmentId: null,
    drawerView: 'quote-wizard',
    viewingShipmentId: null,
    hasCompletedSignup: false,
    activeStep: 'ship-from',
    completedSteps: {},
  };

  if (!seed) return base;

  return {
    ...base,
    selectedShipFromId: seed.selectedShipFromId ?? base.selectedShipFromId,
    destinationAddress: seed.destinationAddress ?? base.destinationAddress,
    handlingUnits: seed.handlingUnits?.length ? seed.handlingUnits : base.handlingUnits,
    additionalServices: seed.additionalServices ?? base.additionalServices,
    quotes: seed.quotes ?? base.quotes,
    selectedQuote: seed.selectedQuote ?? base.selectedQuote,
    drawerView: 'booking-wizard',
    activeStep: 'additional-details',
    completedSteps: { 'ship-from': true, 'ship-to': true, 'handling-units': true, quotes: true },
  };
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

type FreightQuoteAction =
  | { type: 'SET_SELECTED_SHIP_FROM'; shipFromId: string }
  | { type: 'UPDATE_ORIGIN_ADDRESS'; address: LtlAddress }
  | { type: 'UPDATE_DESTINATION_ADDRESS'; address: LtlAddress }
  | { type: 'UPDATE_SHIPMENT_DATE'; shipmentDate: string }
  | { type: 'UPDATE_HANDLING_UNITS'; handlingUnits: LtlHandlingUnit[] }
  | { type: 'ADD_HANDLING_UNIT'; handlingUnit: LtlHandlingUnit }
  | { type: 'REMOVE_HANDLING_UNIT'; handlingUnitId: string }
  | { type: 'ADD_COMMODITY'; handlingUnitId: string; commodity: LtlCommodity }
  | { type: 'REMOVE_COMMODITY'; handlingUnitId: string; commodityId: string }
  | { type: 'UPDATE_COMMODITY'; handlingUnitId: string; commodityId: string; commodity: LtlCommodity }
  | { type: 'UPDATE_ADDITIONAL_SERVICES'; additionalServices: LtlAdditionalServices }
  | { type: 'UPDATE_BOOKING_DETAILS'; bookingDetails: LtlBookingDetails }
  | { type: 'SELECT_QUOTE'; quote: LtlQuote }
  | { type: 'GET_QUOTES_REQUEST' }
  | { type: 'GET_QUOTES_SUCCESS'; quotes: LtlQuote[] }
  | { type: 'GET_QUOTES_ERROR'; error: string }
  | { type: 'BOOK_SHIPMENT_REQUEST' }
  | { type: 'BOOK_SHIPMENT_SUCCESS'; confirmation: LtlBookingConfirmation }
  | { type: 'BOOK_SHIPMENT_ERROR'; error: string }
  | { type: 'SAVE_QUOTE_REQUEST' }
  | { type: 'SAVE_QUOTE_SUCCESS' }
  | { type: 'SAVE_QUOTE_ERROR' }
  | { type: 'LOAD_SAVED_QUOTE_SUCCESS'; freightShipmentId: string; shipment: LoadedSavedQuoteData }
  | { type: 'LOAD_BOOKED_SHIPMENT_REQUEST' }
  | { type: 'LOAD_BOOKED_SHIPMENT_SUCCESS' }
  | { type: 'LOAD_BOOKED_SHIPMENT_ERROR' }
  | { type: 'SET_DRAWER_VIEW'; view: 'quote-wizard' | 'booking-wizard' | 'booking-confirmation' }
  | { type: 'SET_VIEWING_SHIPMENT_ID'; freightShipmentId: string }
  | { type: 'MARK_SIGNUP_COMPLETED' }
  | { type: 'SET_ACTIVE_STEP'; step: WizardStep }
  | { type: 'COMPLETE_STEP'; step: WizardStep };

type LoadedSavedQuoteData = {
  originAddress?: LtlAddress | null;
  destinationAddress?: LtlAddress | null;
  shipDate?: string;
  handlingUnits?: LtlHandlingUnit[];
  additionalServices?: LtlAdditionalServices;
  savedQuoteOptions?: LtlQuote[];
  selectedOfferId?: string;
  shipFromWarehouseId?: number | string | null;
  // Legacy single-quote fields (fallback when savedQuoteOptions is empty)
  offerId: string;
  productTransactionId: string;
  carrierName: string;
  carrierCode?: string;
  carrierScac?: string;
  serviceName: string;
  totalCharges: number;
  transitDays: number;
  estimatedDeliveryDate: string;
};

const buildLoadedQuoteData = (shipment: Record<string, unknown>): { freightShipmentId: string; shipment: LoadedSavedQuoteData } => {
  const handlingUnits = mapRawHandlingUnits(shipment.handlingUnits as Record<string, unknown>[]);
  const savedQuoteOptions = Array.isArray(shipment.savedQuoteOptions)
    ? mapRawQuoteOptions(shipment.savedQuoteOptions as Record<string, unknown>[])
    : [];

  return {
    freightShipmentId: String(shipment.freightShipmentId ?? ''),
    shipment: {
      originAddress: (shipment.originAddress as LtlAddress) ?? null,
      destinationAddress: (shipment.destinationAddress as LtlAddress) ?? null,
      shipDate: shipment.shipDate as string,
      handlingUnits,
      additionalServices: shipment.additionalServices as LtlAdditionalServices,
      savedQuoteOptions,
      selectedOfferId: shipment.selectedOfferId as string,
      offerId: (shipment.offerId as string) ?? '',
      productTransactionId: (shipment.productTransactionId as string) ?? '',
      carrierName: (shipment.carrierName as string) ?? '',
      serviceName: (shipment.serviceName as string) ?? '',
      totalCharges: (shipment.totalCharges as number) ?? 0,
      transitDays: (shipment.transitDays as number) ?? 0,
      estimatedDeliveryDate: (shipment.estimatedDeliveryDate as string) ?? '',
      shipFromWarehouseId: shipment.shipFromWarehouseId as number | null,
    },
  };
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const reducer = (state: FreightQuoteState, action: FreightQuoteAction): FreightQuoteState => {
  switch (action.type) {
    case 'SET_SELECTED_SHIP_FROM':
      return { ...state, selectedShipFromId: action.shipFromId, isQuotesStale: true };
    case 'UPDATE_ORIGIN_ADDRESS':
      return { ...state, originAddress: action.address };
    case 'UPDATE_DESTINATION_ADDRESS':
      return { ...state, destinationAddress: action.address, isQuotesStale: true };
    case 'UPDATE_SHIPMENT_DATE':
      return { ...state, shipmentDate: action.shipmentDate, isQuotesStale: true };
    case 'UPDATE_HANDLING_UNITS':
      return { ...state, handlingUnits: action.handlingUnits, isQuotesStale: true };
    case 'ADD_HANDLING_UNIT':
      return { ...state, handlingUnits: [...state.handlingUnits, action.handlingUnit], isQuotesStale: true };
    case 'REMOVE_HANDLING_UNIT':
      return { ...state, handlingUnits: state.handlingUnits.filter((u) => u.id !== action.handlingUnitId), isQuotesStale: true };
    case 'ADD_COMMODITY':
      return {
        ...state,
        handlingUnits: state.handlingUnits.map((u) =>
          u.id === action.handlingUnitId ? { ...u, commodities: [...u.commodities, action.commodity] } : u,
        ),
      };
    case 'REMOVE_COMMODITY':
      return {
        ...state,
        handlingUnits: state.handlingUnits.map((u) =>
          u.id === action.handlingUnitId ? { ...u, commodities: u.commodities.filter((c) => c.id !== action.commodityId) } : u,
        ),
      };
    case 'UPDATE_COMMODITY':
      return {
        ...state,
        handlingUnits: state.handlingUnits.map((u) =>
          u.id === action.handlingUnitId
            ? { ...u, commodities: u.commodities.map((c) => (c.id === action.commodityId ? action.commodity : c)) }
            : u,
        ),
      };
    case 'UPDATE_ADDITIONAL_SERVICES':
      return { ...state, additionalServices: action.additionalServices };
    case 'UPDATE_BOOKING_DETAILS':
      return { ...state, bookingDetails: action.bookingDetails };
    case 'SELECT_QUOTE':
      return { ...state, selectedQuote: action.quote };
    case 'GET_QUOTES_REQUEST':
      return { ...state, isLoadingQuotes: true, hasQuoteError: false, quoteErrorMessage: '', quotes: [], selectedQuote: null };
    case 'GET_QUOTES_SUCCESS':
      return { ...state, isLoadingQuotes: false, quotes: action.quotes, isQuotesStale: false };
    case 'GET_QUOTES_ERROR':
      return { ...state, isLoadingQuotes: false, hasQuoteError: true, quoteErrorMessage: action.error };
    case 'BOOK_SHIPMENT_REQUEST':
      return { ...state, isBookingShipment: true, hasBookingError: false, bookingErrorMessage: '' };
    case 'BOOK_SHIPMENT_SUCCESS':
      return {
        ...state,
        isBookingShipment: false,
        bookingConfirmation: action.confirmation,
        drawerView: 'booking-confirmation',
        viewingShipmentId: String(action.confirmation.freightShipmentId),
      };
    case 'BOOK_SHIPMENT_ERROR':
      return { ...state, isBookingShipment: false, hasBookingError: true, bookingErrorMessage: action.error };
    case 'SAVE_QUOTE_REQUEST':
      return { ...state, isSavingQuote: true };
    case 'SAVE_QUOTE_SUCCESS':
      return { ...state, isSavingQuote: false };
    case 'SAVE_QUOTE_ERROR':
      return { ...state, isSavingQuote: false };
    case 'LOAD_SAVED_QUOTE_SUCCESS': {
      const s = action.shipment;
      const hasSavedOptions = Array.isArray(s.savedQuoteOptions) && s.savedQuoteOptions.length > 0;

      // Populate quotes from savedQuoteOptions if available, otherwise fall back to single selected quote.
      // When neither is present (e.g. cached entity without quote data), leave quotes empty.
      const hasLegacyQuote = !hasSavedOptions && Boolean(s.offerId);

      let loadedQuotes: LtlQuote[];
      if (hasSavedOptions) {
        loadedQuotes = s.savedQuoteOptions!;
      } else if (hasLegacyQuote) {
        loadedQuotes = [
          {
            offerId: s.offerId,
            productTransactionId: s.productTransactionId,
            carrierName: s.carrierName,
            carrierCode: s.carrierCode ?? s.carrierScac ?? '',
            serviceName: s.serviceName,
            totalCharges: s.totalCharges,
            transitDays: s.transitDays,
            estimatedDeliveryDate: s.estimatedDeliveryDate,
            isGuaranteed: false,
            quoteExpirationDate: null,
          },
        ];
      } else {
        loadedQuotes = [];
      }

      // Find the previously selected quote by offerId.
      // When savedQuoteOptions exist, only pre-select if the user explicitly chose one.
      // For the legacy single-quote fallback path (no savedQuoteOptions), always select the sole quote.
      let selectedOfferId: string | undefined;
      if (hasSavedOptions) {
        selectedOfferId = s.selectedOfferId;
      } else if (hasLegacyQuote) {
        selectedOfferId = s.selectedOfferId || s.offerId;
      } else {
        selectedOfferId = undefined;
      }
      const matchedQuote = selectedOfferId ? (loadedQuotes.find((q) => q.offerId === selectedOfferId) ?? null) : null;

      return {
        ...state,
        isLoadingQuotes: false,
        isQuotesStale: false,
        savedQuoteFreightShipmentId: action.freightShipmentId,
        drawerView: 'quote-wizard',
        quotes: loadedQuotes,
        selectedQuote: matchedQuote,
        originAddress: s.originAddress
          ? {
              name: s.originAddress.name ?? '',
              company: s.originAddress.company ?? '',
              line1: s.originAddress.line1 ?? '',
              line2: s.originAddress.line2 ?? '',
              city: s.originAddress.city ?? '',
              state: s.originAddress.state ?? '',
              postalCode: s.originAddress.postalCode ?? '',
              countryCode: s.originAddress.countryCode ?? 'US',
              phone: s.originAddress.phone ?? '',
              email: s.originAddress.email ?? '',
              locationType: s.originAddress.locationType ?? '',
            }
          : state.originAddress,
        destinationAddress: s.destinationAddress
          ? {
              name: s.destinationAddress.name ?? '',
              company: s.destinationAddress.company ?? '',
              line1: s.destinationAddress.line1 ?? '',
              line2: s.destinationAddress.line2 ?? '',
              city: s.destinationAddress.city ?? '',
              state: s.destinationAddress.state ?? '',
              postalCode: s.destinationAddress.postalCode ?? '',
              countryCode: s.destinationAddress.countryCode ?? 'US',
              phone: s.destinationAddress.phone ?? '',
              email: s.destinationAddress.email ?? '',
              locationType: s.destinationAddress.locationType ?? '',
            }
          : state.destinationAddress,
        selectedShipFromId: String(s.shipFromWarehouseId || ''),
        shipmentDate: s.shipDate ?? state.shipmentDate,
        handlingUnits: s.handlingUnits ?? state.handlingUnits,
        additionalServices: s.additionalServices ?? state.additionalServices,
        // Restore accordion progress: mark all steps before quotes as complete
        activeStep: loadedQuotes.length > 0 ? ('quotes' as WizardStep) : ('ship-from' as WizardStep),
        completedSteps: loadedQuotes.length > 0 ? { 'ship-from': true, 'ship-to': true, 'handling-units': true } : {},
      };
    }
    case 'LOAD_BOOKED_SHIPMENT_REQUEST':
      return { ...state, isRefreshingShipment: true };
    case 'LOAD_BOOKED_SHIPMENT_SUCCESS':
      return { ...state, isRefreshingShipment: false };
    case 'LOAD_BOOKED_SHIPMENT_ERROR':
      return { ...state, isRefreshingShipment: false };
    case 'SET_DRAWER_VIEW':
      return { ...state, drawerView: action.view };
    case 'SET_VIEWING_SHIPMENT_ID':
      return { ...state, viewingShipmentId: action.freightShipmentId };
    case 'MARK_SIGNUP_COMPLETED':
      return { ...state, hasCompletedSignup: true };
    case 'SET_ACTIVE_STEP':
      return { ...state, activeStep: action.step };
    case 'COMPLETE_STEP':
      return {
        ...state,
        completedSteps: { ...state.completedSteps, [action.step]: true },
      };
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// Mapping helpers (moved from epics)
// ---------------------------------------------------------------------------

const mapWeightUnit = (sellerUnit: string): string => {
  if (([WeightUnit.Pounds, WeightUnit.Pound] as string[]).includes(sellerUnit)) return 'Pounds';
  if (([WeightUnit.Ounces, WeightUnit.Ounce] as string[]).includes(sellerUnit)) return 'Ounces';
  if (([WeightUnit.Grams, WeightUnit.Gram] as string[]).includes(sellerUnit)) return 'Grams';
  if (([WeightUnit.Kilograms, WeightUnit.Kilogram] as string[]).includes(sellerUnit)) return 'Kilograms';
  return 'Pounds';
};

const mapDimensionUnit = (sellerUnit: string): string => {
  if (([DimensionUnit.Centimeters, DimensionUnit.Centimeter] as string[]).includes(sellerUnit)) return 'Centimeters';
  return 'Inches';
};

const mapAddressToBackend = (addr: LtlAddress | null) => {
  if (!addr) return null;
  return {
    line1: addr.line1 || '',
    line2: addr.line2 || '',
    city: addr.city,
    state: addr.state,
    postalCode: addr.postalCode,
    countryCode: addr.countryCode || 'US',
    company: addr.company,
    phone: addr.phone,
    email: addr.email || '',
    name: addr.name || '',
    locationType: addr.locationType || null,
  };
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type FreightQuoteContextValue = {
  state: FreightQuoteState;
  localDispatch: React.Dispatch<FreightQuoteAction>;
  getQuotes: () => Promise<void>;
  bookShipment: (bookingDetailsOverride?: LtlBookingDetails) => Promise<void>;
  saveQuote: () => Promise<void>;
};

const FreightQuoteContext = createContext<FreightQuoteContextValue | null>(null);

export const useFreightQuote = (): FreightQuoteContextValue => {
  const context = useContext(FreightQuoteContext);
  if (!context) {
    throw new Error('useFreightQuote must be used within a FreightQuoteProvider');
  }
  return context;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

type FreightQuoteProviderProps = {
  children: ReactNode;
  wizardOpenConfig?: WizardOpenConfig | null;
  seed?: FreightQuoteSeedData | null;
};

export const FreightQuoteProvider = ({ children, wizardOpenConfig = null, seed = null }: FreightQuoteProviderProps) => {
  const store = useStore();
  const [state, localDispatch] = useReducer(reducer, seed, createInitialState);
  const reduxDispatch = useReduxDispatch();
  const mountedRef = useRef(true);

  // Stable ref to current state for use in async callbacks
  const stateRef = useRef(state);
  stateRef.current = state;
  const freightProvidersSelection = useSelector(Global.selectFreightSettingsProviders);
  const freightProviders = useMemo(() => freightProvidersSelection ?? [], [freightProvidersSelection]);
  const sellerData = useSelector(Global.selectSellerData);
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);

  const getConnectedSellerFreightProviderId = useCallback((): number | undefined => {
    const connected = freightProviders.find((p: { isConnected: boolean }) => p.isConnected);
    return connected?.sellerFreightProviderId ?? undefined;
  }, [freightProviders]);

  const resolveOriginAddress = useCallback(
    (currentState: FreightQuoteState) => {
      const selectedShipFromId = currentState.selectedShipFromId;
      const shipFroms = sellerData?.shipFroms ?? [];
      const shipFrom = shipFroms.find((sf: { shipFromId: number }) => String(sf.shipFromId) === String(selectedShipFromId));
      const sfAddr = shipFrom?.originAddress;
      if (sfAddr) {
        return mapAddressToBackend({
          line1: sfAddr.line1 ?? '',
          line2: sfAddr.line2 ?? '',
          city: sfAddr.city ?? '',
          state: sfAddr.state ?? '',
          postalCode: sfAddr.postalCode ?? '',
          countryCode: sfAddr.countryCode ?? 'US',
          company: sfAddr.company ?? shipFrom.name ?? '',
          phone: sfAddr.phone ?? '',
          email: sfAddr.email ?? '',
          name: sfAddr.name ?? '',
          locationType: '',
        });
      }
      return mapAddressToBackend(currentState.originAddress);
    },
    [sellerData],
  );

  const getUnitMappings = useCallback(() => {
    const weightUnit = mapWeightUnit(sellerWeightUnit);
    const dimensionUnit = mapDimensionUnit(sellerDimensionUnit);
    return { weightUnit, dimensionUnit };
  }, [sellerWeightUnit, sellerDimensionUnit]);

  const mapHandlingUnitsToPayload = useCallback(
    (handlingUnits: LtlHandlingUnit[]) => {
      const { weightUnit, dimensionUnit } = getUnitMappings();
      return (handlingUnits || []).map((hu) => ({
        packagingType: hu.packagingType || 'PLT',
        quantity: hu.quantity || 1,
        length: hu.length,
        width: hu.width,
        height: hu.height,
        dimensionUnit,
        isStackable: hu.isStackable || false,
        commodities: (hu.commodities || []).map((c) => ({
          freightClass: c.freightClass || '_50',
          description: c.description || 'General Merchandise',
          nmfcCode: c.nmfcCode,
          quantity: c.quantity || 1,
          weight: c.weight,
          weightUnit,
          isHazmat: c.isHazmat || false,
        })),
      }));
    },
    [getUnitMappings],
  );

  const loadBookedShipmentInternal = useCallback(
    async (freightShipmentId: string) => {
      localDispatch({ type: 'LOAD_BOOKED_SHIPMENT_REQUEST' });
      try {
        const data = await axios.get(`/freight/shipment/${freightShipmentId}`).then((r) => r.data);
        if (!mountedRef.current) return;
        if (data.success === false) {
          const errorMessage = data.errors?.[0]?.message || 'Failed to load shipment.';
          NotificationService.showGenericFailureNotification(undefined, errorMessage);
          localDispatch({ type: 'LOAD_BOOKED_SHIPMENT_ERROR' });
          return;
        }
        reduxDispatch(entitiesLoad({ entities: { freightShipments: { [data.freightShipmentId]: data } } }));
        localDispatch({ type: 'LOAD_BOOKED_SHIPMENT_SUCCESS' });
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to load shipment.';
        NotificationService.showGenericFailureNotification(undefined, errorMessage);
        localDispatch({ type: 'LOAD_BOOKED_SHIPMENT_ERROR' });
      }
    },
    [reduxDispatch],
  );

  const loadSavedQuoteInternal = useCallback(
    async (freightShipmentId: string) => {
      // Instantly populate the form from the cached Redux entity (available from the grid call)
      // so the user sees form data without waiting for the API round-trip.
      const cachedEntity = Global.selectEntitiesFreightShipmentById(store.getState(), { freightShipmentId });
      if (cachedEntity) {
        const cached = buildLoadedQuoteData(cachedEntity);
        localDispatch({
          type: 'LOAD_SAVED_QUOTE_SUCCESS',
          freightShipmentId: cached.freightShipmentId,
          shipment: cached.shipment,
        });
      } else {
        // No cached data — show loading spinner while we fetch
        localDispatch({ type: 'GET_QUOTES_REQUEST' });
      }

      // Fetch full data (with DynamoDB quote options) in the background
      try {
        const shipment = await axios.get(`/freight/quote/${freightShipmentId}`).then((r) => r.data);
        if (!mountedRef.current) return;
        if (shipment.success === false) {
          const errorMessage = shipment.errors?.[0]?.message || 'Failed to load saved quote.';
          NotificationService.showGenericFailureNotification(undefined, errorMessage);
          // Only show error state if we didn't already populate from cache
          if (!cachedEntity) {
            localDispatch({ type: 'GET_QUOTES_ERROR', error: errorMessage });
          }
          return;
        }

        const full = buildLoadedQuoteData(shipment);
        localDispatch({
          type: 'LOAD_SAVED_QUOTE_SUCCESS',
          freightShipmentId: full.freightShipmentId,
          shipment: full.shipment,
        });
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to load saved quote.';
        NotificationService.showGenericFailureNotification(undefined, errorMessage);
        if (!cachedEntity) {
          localDispatch({ type: 'GET_QUOTES_ERROR', error: errorMessage });
        }
      }
    },
    [store],
  );

  // ------- Pusher event tracking -------

  const pendingQuoteGuidsRef = useRef<Set<string>>(new Set());
  const pendingBookGuidsRef = useRef<Set<string>>(new Set());
  const quoteFinishRef = useRef<((guids: string[]) => void) | null>(null);
  const bookFinishRef = useRef<((guids: string[]) => void) | null>(null);

  // jobGuidManagers for the fallback mechanism — created once on mount
  const quoteJobGuidManagerRef = useRef(jobGuidManagerFactory());
  const bookJobGuidManagerRef = useRef(jobGuidManagerFactory());

  // The backend serializes FreightQuoteResult / FreightBookingResult with default
  // Newtonsoft settings (PascalCase), so we read PascalCase fields from `data` here.
  // The error notification is hand-constructed on the backend with a lowercase
  // `errorMessage` property, so that one field stays lowercase.
  const handleFreightQuotePusher = useCallback(
    (pusherMessage: { messageGuid: string; data: { errorMessage?: string; Quotes?: unknown[] } }) => {
      const { messageGuid, data } = pusherMessage;
      if (!pendingQuoteGuidsRef.current.has(messageGuid)) return;
      pendingQuoteGuidsRef.current.delete(messageGuid);

      if (!mountedRef.current) return;

      if (data?.errorMessage) {
        localDispatch({ type: 'GET_QUOTES_ERROR', error: data.errorMessage });
      } else {
        const quotes = mapPascalCaseQuoteOptions((data?.Quotes ?? []) as Record<string, unknown>[]);
        localDispatch({ type: 'GET_QUOTES_SUCCESS', quotes });
      }

      if (pendingQuoteGuidsRef.current.size === 0 && quoteFinishRef.current) {
        quoteFinishRef.current([messageGuid]);
        quoteFinishRef.current = null;
      }
    },
    [],
  );

  const handleFreightBookPusher = useCallback(
    (pusherMessage: { messageGuid: string; data: Record<string, unknown> & { errorMessage?: string } }) => {
      const { messageGuid, data } = pusherMessage;
      const isExpected = pendingBookGuidsRef.current.has(messageGuid);
      const isMidBooking = stateRef.current?.isBookingShipment === true;
      if (!isExpected && !isMidBooking) return;
      pendingBookGuidsRef.current.delete(messageGuid);

      if (!mountedRef.current) return;

      if (data?.errorMessage) {
        localDispatch({ type: 'BOOK_SHIPMENT_ERROR', error: data.errorMessage });
      } else {
        // Backend FreightBookingResult properties are PascalCase. Map them to the
        // camelCase LtlBookingConfirmation shape the rest of the app expects.
        const confirmation: LtlBookingConfirmation = {
          freightShipmentId: (data.FreightShipmentId as number) ?? 0,
          productTransactionId: (data.ProductTransactionId as string) ?? '',
          pickupTransactionId: (data.PickupTransactionId as string) ?? '',
          bolNumber: (data.BolNumber as string) ?? '',
          proNumber: (data.ProNumber as string) ?? '',
          pickupConfirmationNumber: (data.ConfirmationNumber as string) ?? '',
        };
        localDispatch({ type: 'BOOK_SHIPMENT_SUCCESS', confirmation });

        if (confirmation.freightShipmentId) {
          reduxDispatch(entitiesLoad({ entities: { freightShipments: { [confirmation.freightShipmentId]: data } } }));
          loadBookedShipmentInternal(String(confirmation.freightShipmentId));
        }
        reduxDispatch(getFreightGridRequest());
      }

      if (pendingBookGuidsRef.current.size === 0 && bookFinishRef.current) {
        bookFinishRef.current([messageGuid]);
        bookFinishRef.current = null;
      }
    },
    [reduxDispatch, loadBookedShipmentInternal],
  );

  // Set up Pusher message caches with fallback options on mount.
  // Using createPusherMessageCache (which calls PusherService.bindToEvent) instead of
  // usePusherEvent (which calls bindToEventOnAllChannels without fallbackOptions) ensures
  // the "message too large" fallback HTTP fetch is wired up correctly.
  useEffect(() => {
    const quoteFallbackOptions = {
      timeoutMs: 30000,
      jobGuidManager: quoteJobGuidManagerRef.current,
      resetTimeoutCheck: (msg: Record<string, unknown>) => pendingQuoteGuidsRef.current.has(msg?.messageGuid as string),
      completeCheck: (msg: Record<string, unknown>) => pendingQuoteGuidsRef.current.has(msg?.messageGuid as string),
      timeoutCompleteCheck: ({ messages }: { messages: object[]; retryCount: number }) =>
        (messages as Array<{ messageGuid?: string }>).some((msg) => pendingQuoteGuidsRef.current.has(msg?.messageGuid ?? '')),
      timeoutRetryCheck: ({ messages, retryCount }: { messages: object[]; retryCount: number }) =>
        !(messages as Array<{ messageGuid?: string }>).some((msg) => pendingQuoteGuidsRef.current.has(msg?.messageGuid ?? '')) &&
        retryCount < 3,
      timeoutFailureCheck: ({ messages, retryCount }: { messages: object[]; retryCount: number }) =>
        !(messages as Array<{ messageGuid?: string }>).some((msg) => pendingQuoteGuidsRef.current.has(msg?.messageGuid ?? '')) &&
        retryCount >= 3,
      onTimeout: () => localDispatch({ type: 'GET_QUOTES_ERROR', error: 'Quote request timed out' }),
      logContext: {},
    };

    const bookFallbackOptions = {
      timeoutMs: 30000,
      jobGuidManager: bookJobGuidManagerRef.current,
      resetTimeoutCheck: (msg: Record<string, unknown>) => pendingBookGuidsRef.current.has(msg?.messageGuid as string),
      completeCheck: (msg: Record<string, unknown>) => pendingBookGuidsRef.current.has(msg?.messageGuid as string),
      timeoutCompleteCheck: ({ messages }: { messages: object[]; retryCount: number }) =>
        (messages as Array<{ messageGuid?: string }>).some((msg) => pendingBookGuidsRef.current.has(msg?.messageGuid ?? '')),
      timeoutRetryCheck: ({ messages, retryCount }: { messages: object[]; retryCount: number }) =>
        !(messages as Array<{ messageGuid?: string }>).some((msg) => pendingBookGuidsRef.current.has(msg?.messageGuid ?? '')) &&
        retryCount < 3,
      timeoutFailureCheck: ({ messages, retryCount }: { messages: object[]; retryCount: number }) =>
        !(messages as Array<{ messageGuid?: string }>).some((msg) => pendingBookGuidsRef.current.has(msg?.messageGuid ?? '')) &&
        retryCount >= 3,
      onTimeout: () => localDispatch({ type: 'BOOK_SHIPMENT_ERROR', error: 'Booking request timed out' }),
      logContext: {},
    };

    const { findMessage: findQuoteMessage } = createPusherMessageCache(['freight-quote'], quoteFallbackOptions);
    const { findMessage: findBookMessage } = createPusherMessageCache(['freight-book'], bookFallbackOptions);

    const quoteSub = findQuoteMessage((msg: Record<string, unknown>) =>
      pendingQuoteGuidsRef.current.has(msg?.messageGuid as string),
    ).subscribe((msg: unknown) =>
      handleFreightQuotePusher(msg as { messageGuid: string; data: { errorMessage?: string; Quotes?: unknown[] } }),
    );

    const bookSub = findBookMessage((msg: Record<string, unknown>) =>
      pendingBookGuidsRef.current.has(msg?.messageGuid as string),
    ).subscribe((msg: unknown) =>
      handleFreightBookPusher(msg as { messageGuid: string; data: Record<string, unknown> & { errorMessage?: string } }),
    );

    return () => {
      quoteSub.unsubscribe();
      bookSub.unsubscribe();
    };
    // Run once on mount — handlers and refs are stable so closures stay current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- Async operations -------

  const getQuotes = useCallback(async () => {
    const currentState = stateRef.current;
    const sellerFreightProviderId = getConnectedSellerFreightProviderId();
    const services = currentState.additionalServices || {};

    localDispatch({ type: 'GET_QUOTES_REQUEST' });

    const payload = {
      sellerFreightProviderId,
      shipment: {
        // POC: backend currently expects originAddress alongside shipFromId.
        // resolveOriginAddress reads the selected ship-from warehouse's address.
        shipFromId: currentState.selectedShipFromId ? Number(currentState.selectedShipFromId) : null,
        originAddress: resolveOriginAddress(currentState),
        destinationAddress: mapAddressToBackend(currentState.destinationAddress),
        shipmentDate: currentState.shipmentDate ? `${currentState.shipmentDate.split('T')[0]}T09:00:00` : '',
        handlingUnits: mapHandlingUnitsToPayload(currentState.handlingUnits),
        additionalServices: {
          liftgatePickup: services.isLiftgatePickup || false,
          liftgateDelivery: services.isLiftgateDelivery || false,
          insidePickup: services.isInsidePickup || false,
          insideDelivery: services.isInsideDelivery || false,
          residentialPickup: services.isResidentialPickup || false,
          residentialDelivery: services.isResidentialDelivery || false,
          appointmentDelivery: false,
          notifyBeforeDelivery: services.isCallBeforeDelivery || false,
          sortAndSegregate: false,
          protectionFromCold: false,
          holdAtTerminal: false,
        },
      },
    };

    try {
      const data = await axios.post('/freight/quote', payload).then((r) => r.data);
      if (!mountedRef.current) return;
      if (data.success === false) {
        localDispatch({ type: 'GET_QUOTES_ERROR', error: data.errors?.[0]?.message || 'Failed to retrieve quotes.' });
        return;
      }
      // Store the jobGuids — results will arrive via Pusher 'freight-quote' event
      const jobGuids: string[] = (data.jobGuids ?? data.JobGuids ?? []) as string[];
      jobGuids.forEach((guid) => {
        pendingQuoteGuidsRef.current.add(guid);
        quoteJobGuidManagerRef.current.addJobGuid(guid);
      });
      const { onStart: onQuoteStart, onFinish: onQuoteFinish } = freightListener.createSingleJobScopedHandlers();
      quoteFinishRef.current = onQuoteFinish;
      onQuoteStart(jobGuids);
      // Loading state remains active until Pusher delivers the results
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to retrieve quotes.';
      localDispatch({ type: 'GET_QUOTES_ERROR', error: errorMessage });
    }
  }, [getConnectedSellerFreightProviderId, mapHandlingUnitsToPayload, resolveOriginAddress]);

  const bookShipment = useCallback(
    async (bookingDetailsOverride?: LtlBookingDetails) => {
      const currentState = stateRef.current;
      const selectedQuote = currentState.selectedQuote;
      const sellerFreightProviderId = getConnectedSellerFreightProviderId();

      localDispatch({ type: 'BOOK_SHIPMENT_REQUEST' });

      // Use override if provided (avoids race condition with dispatched state update)
      const booking = bookingDetailsOverride ?? currentState.bookingDetails;
      const rawPickupDate = booking?.pickupDate || currentState.shipmentDate || new Date().toISOString();
      const pickupDate = rawPickupDate.split('T')[0];
      const pickupReadyTime = booking?.pickupReadyTime || '09:00';
      const pickupCloseTime = booking?.pickupCloseTime || '17:00';

      const payload = {
        sellerFreightProviderId,
        productTransactionId: selectedQuote?.productTransactionId,
        offerId: selectedQuote?.offerId,
        bookingDetails: {
          originAddress: resolveOriginAddress(currentState),
          destinationAddress: mapAddressToBackend(currentState.destinationAddress),
          pickupInstructions: booking?.pickupInstructions ?? '',
          deliveryInstructions: booking?.deliveryInstructions ?? '',
          handlingInstructions: booking?.specialInstructions ?? '',
          references: booking?.referenceNumber ? [{ type: 'Reference 1', value: booking.referenceNumber }] : [],
        },
        pickupDetails: {
          pickupDate: `${pickupDate} ${pickupReadyTime}:00`,
          readyTime: `${pickupReadyTime}:00`,
          closeTime: `${pickupCloseTime}:00`,
          isSelfScheduled: booking?.isSelfScheduled ?? false,
        },
        carrierName: selectedQuote?.carrierName,
        serviceName: selectedQuote?.serviceName,
        totalCharges: selectedQuote?.totalCharges,
        transitDays: selectedQuote?.transitDays,
        estimatedDeliveryDate: selectedQuote?.estimatedDeliveryDate,
        totalWeight:
          currentState.handlingUnits?.reduce((sum, hu) => sum + (hu.commodities?.reduce((cSum, c) => cSum + (c.weight || 0), 0) || 0), 0) ??
          0,
        handlingUnits: mapHandlingUnitsToPayload(currentState.handlingUnits),
        shipFromWarehouseId: currentState.selectedShipFromId ? Number(currentState.selectedShipFromId) : null,
        ...(currentState.savedQuoteFreightShipmentId ? { freightShipmentId: currentState.savedQuoteFreightShipmentId } : {}),
      };

      try {
        const data = await axios.post('/freight/book', payload).then((r) => r.data);
        if (!mountedRef.current) return;
        if (data.success === false) {
          localDispatch({ type: 'BOOK_SHIPMENT_ERROR', error: data.errors?.[0]?.message || 'Failed to book shipment.' });
          return;
        }
        // Store the jobGuids — results will arrive via Pusher 'freight-book' event
        const jobGuids: string[] = (data.jobGuids ?? data.JobGuids ?? []) as string[];
        jobGuids.forEach((guid) => {
          pendingBookGuidsRef.current.add(guid);
          bookJobGuidManagerRef.current.addJobGuid(guid);
        });
        const { onStart: onBookStart, onFinish: onBookFinish } = freightListener.createSingleJobScopedHandlers();
        bookFinishRef.current = onBookFinish;
        onBookStart(jobGuids);
        // Loading state remains active until Pusher delivers the results
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to book shipment.';
        localDispatch({ type: 'BOOK_SHIPMENT_ERROR', error: errorMessage });
      }
    },
    [getConnectedSellerFreightProviderId, resolveOriginAddress, mapHandlingUnitsToPayload],
  );

  const saveQuote = useCallback(async () => {
    const currentState = stateRef.current;
    const selectedQuote = currentState.selectedQuote;
    const sellerFreightProviderId = getConnectedSellerFreightProviderId();

    if (!currentState.quotes.length) return;

    localDispatch({ type: 'SAVE_QUOTE_REQUEST' });

    const payload = {
      sellerFreightProviderId,
      productTransactionId: currentState.quotes[0]?.productTransactionId,
      offerId: selectedQuote?.offerId,
      carrierName: selectedQuote?.carrierName,
      carrierScac: selectedQuote?.carrierCode,
      serviceName: selectedQuote?.serviceName,
      totalCharges: selectedQuote?.totalCharges,
      transitDays: selectedQuote?.transitDays,
      estimatedDeliveryDate: selectedQuote?.estimatedDeliveryDate,
      totalWeight: currentState.handlingUnits.reduce(
        (sum, hu) => sum + (hu.commodities?.reduce((cSum, c) => cSum + (c.weight || 0), 0) || 0),
        0,
      ),
      shipDate: currentState.shipmentDate,
      originAddress: resolveOriginAddress(currentState) ?? null,
      destinationAddress: mapAddressToBackend(currentState.destinationAddress),
      handlingUnits: mapHandlingUnitsToPayload(currentState.handlingUnits),
      additionalServices: currentState.additionalServices,
      shipFromWarehouseId: currentState.selectedShipFromId ? Number(currentState.selectedShipFromId) : null,
      ...(currentState.savedQuoteFreightShipmentId ? { freightShipmentId: currentState.savedQuoteFreightShipmentId } : {}),
      allQuotes: currentState.quotes.map((q) => ({
        offerId: q.offerId,
        productTransactionId: q.productTransactionId,
        carrierName: q.carrierName,
        carrierCode: q.carrierCode,
        serviceName: q.serviceName,
        totalCharges: q.totalCharges,
        transitDays: q.transitDays,
        estimatedDeliveryDate: q.estimatedDeliveryDate,
        isGuaranteed: q.isGuaranteed,
        quoteExpirationDate: q.quoteExpirationDate ?? null,
        serviceType: q.serviceType ?? null,
        originTerminalCode: q.originTerminalCode ?? null,
        destinationTerminalCode: q.destinationTerminalCode ?? null,
        insuredAmount: q.insuredAmount ?? null,
        insurancePremium: q.insurancePremium ?? null,
        insuranceCertificateNumber: q.insuranceCertificateNumber ?? null,
      })),
    };

    try {
      await axios.post('/freight/save-quote', payload).then((r) => r.data);
      if (!mountedRef.current) return;
      NotificationService.showGenericSuccessNotification();
      localDispatch({ type: 'SAVE_QUOTE_SUCCESS' });
      reduxDispatch(getFreightGridRequest());
    } catch (err) {
      if (!mountedRef.current) return;
      const errorMessage = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save quote.';
      NotificationService.showGenericFailureNotification(undefined, errorMessage);
      localDispatch({ type: 'SAVE_QUOTE_ERROR' });
    }
  }, [getConnectedSellerFreightProviderId, resolveOriginAddress, mapHandlingUnitsToPayload, reduxDispatch]);

  // ------- Initialization on mount -------

  useEffect(() => {
    if (!wizardOpenConfig) return;

    if (wizardOpenConfig.mode === 'load-saved-quote') {
      loadSavedQuoteInternal(wizardOpenConfig.freightShipmentId);
    } else if (wizardOpenConfig.mode === 'view-booked') {
      localDispatch({ type: 'SET_DRAWER_VIEW', view: 'booking-confirmation' });
      localDispatch({ type: 'SET_VIEWING_SHIPMENT_ID', freightShipmentId: wizardOpenConfig.freightShipmentId });
      loadBookedShipmentInternal(wizardOpenConfig.freightShipmentId);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- Cleanup -------

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ------- Context value -------

  const value = useMemo(
    () => ({
      state,
      localDispatch,
      getQuotes,
      bookShipment,
      saveQuote,
    }),
    [state, getQuotes, bookShipment, saveQuote],
  );

  return <FreightQuoteContext.Provider value={value}>{children}</FreightQuoteContext.Provider>;
};
